import { formatLocaleCurrency, formatLocaleDate, formatPhoneNumber, formatPlaceOfInsurance } from "@/utils/formatUtils";
import { tBoolean, tInterval } from "@/utils/translationUtils";
import { cssVariables, isDefined } from "@/utils/utils";
import { Col, Divider, FormInstance, Row, Statistic } from "antd";
import { useTranslation } from "react-i18next";
import AntIcon from "../../../../../common/components/icons/AntIcon";
import { ClientsListView } from "../../../../client/components/ClientsListView";
import { ClientFormType, ClientType } from "../../../../client/enums";
import { NaturalClient, SelfEmployedClient } from "../../../../client/types";
import { createClientsData } from "../../../../client/utils";
import { InstitutionEnum } from "../../../../institution/enums";
import { GenGainersData } from "../../types";
import type { RealtyCalcSummaryViewData } from "./CalcSummaryModal";
import { GainersCommissionRateSection } from "./GainersCommissionRateSection";

type Props = {
  data: RealtyCalcSummaryViewData;
  divideCommission: boolean;
  setDivideCommission: (value: boolean) => void;
  gainersDataForm: FormInstance<GenGainersData>;
};

const SPAN_LABEL = 5;
const SPAN_VALUE = 19;

const RealtyCalcSummaryView = ({
  data: { selectedCalcResult, formData, calcData, clients },
  divideCommission,
  setDivideCommission,
  gainersDataForm,
}: Props) => {
  const { t } = useTranslation();

  const insuredClient = formData.clientsData.policyHolderIsAlsoInsured ? clients.policyHolder : clients.insured;

  const policyHolderIdentityCard =
    clients.policyHolder?.type === ClientType.NATURAL || clients.policyHolder?.type === ClientType.SELF_EMPLOYED
      ? formData.clientsData.policyHolderIdentityCardNumber
      : undefined;

  const policyHolderClient = policyHolderIdentityCard
    ? { ...(clients.policyHolder as NaturalClient | SelfEmployedClient), identityCardNumber: policyHolderIdentityCard }
    : clients.policyHolder;

  const liabilityInsuranceAmount =
    selectedCalcResult.data?.realtyLiabilityInsuranceAmount ||
    selectedCalcResult.data?.householdLiabilityInsuranceAmount ||
    selectedCalcResult.data?.liabilityInsuranceAmount;

  return (
    <div>
      <Row>
        <Col flex={1}>
          <Statistic
            className="statistic-small"
            title={t("common.insuranceInstitution")}
            value={selectedCalcResult.insuranceInstitution.name}
          />
        </Col>

        {selectedCalcResult.coverage ? (
          <Col flex={1}>
            <Statistic
              className="statistic-small"
              title={<span style={{ textTransform: "capitalize" }}>{t("calc.realty.sections.coverage")}</span>}
              value={selectedCalcResult.coverage}
            />
          </Col>
        ) : undefined}

        <Col flex={5}>
          <Statistic
            className="statistic-small"
            title={t("calc.realty.enums.buildingType._label")}
            value={t("calc.realty.enums.buildingType." + calcData.generalBuildingData.type)}
          />
        </Col>
      </Row>

      <Row className="margin-top-small">
        <Col flex={1}>
          <Divider titlePlacement="start">{t("calc.realty.attrs.clientsData._label")}</Divider>
          <ClientsListView
            clientsData={createClientsData([
              {
                client: policyHolderClient,
                type: ClientFormType.POLICY_HOLDER,
              },
              {
                client: insuredClient,
                type: ClientFormType.INSURED,
              },
              {
                client: clients.vinculation,
                type: ClientFormType.VINCULATION,
              },
            ])}
          />
        </Col>
      </Row>
      <Row>
        <Col flex={1}>
          <Row>
            <Col span={SPAN_LABEL}>{t("calc.realty.attrs.clientsData.policyHolderPhone")}</Col>
            <Col span={SPAN_VALUE}>{formatPhoneNumber(formData.clientsData.policyHolderPhone)}</Col>
          </Row>
          <Row>
            <Col span={SPAN_LABEL}>{t("calc.realty.attrs.clientsData.policyHolderEmail")}</Col>
            <Col span={SPAN_VALUE}>{formData.clientsData.policyHolderEmail}</Col>
          </Row>
        </Col>
      </Row>

      <Row className="margin-top-small">
        <Col flex={1}>
          <Divider titlePlacement="start">{t("calc.realty.sections.realtyInsurance")}</Divider>
          <Row>
            <Col span={SPAN_LABEL}>{t("calc.realty.attrs.generalBuildingData.placeOfInsurance._label")}</Col>
            <Col span={SPAN_VALUE}> {formatPlaceOfInsurance(calcData.generalBuildingData.placeOfInsurance)}</Col>
          </Row>
          <Row>
            <Col span={SPAN_LABEL}>{t("calc.realty.enums.buildingState._label")}</Col>
            <Col span={SPAN_VALUE}>{t("calc.realty.enums.buildingState." + calcData.generalBuildingData.state)}</Col>
          </Row>
          <Row>
            <Col span={SPAN_LABEL}>{t("calc.realty.attrs.generalBuildingData.constructionYear")}</Col>
            <Col span={SPAN_VALUE}> {calcData.generalBuildingData.constructionYear}</Col>
          </Row>
          <Row>
            <Col span={SPAN_LABEL}>{t("calc.realty.attrs.generalBuildingData.inCity")}</Col>
            <Col span={SPAN_VALUE}> {tBoolean(calcData.generalBuildingData.inCity)}</Col>
          </Row>
          <Row>
            <Col span={SPAN_LABEL}>{t("calc.realty.attrs.generalBuildingData.permanentlyOccupied")}</Col>
            <Col span={SPAN_VALUE}> {tBoolean(calcData.generalBuildingData.permanentlyOccupied)}</Col>
          </Row>
          {calcData.houseOrRecreationalBuildingData ? (
            <>
              {calcData.houseOrRecreationalBuildingData.constructionMaterial ? (
                <Row>
                  <Col span={SPAN_LABEL}> {t("calc.realty.enums.constructionMaterial._label")}</Col>
                  <Col span={SPAN_VALUE}>
                    {t(
                      "calc.realty.enums.constructionMaterial." +
                        calcData.houseOrRecreationalBuildingData.constructionMaterial,
                    )}
                  </Col>
                </Row>
              ) : undefined}
              <Row>
                <Col span={SPAN_LABEL}>{t("calc.realty.attrs.houseOrRecreationalBuildingData.buildArea")}</Col>
                <Col span={SPAN_VALUE}>
                  {calcData.houseOrRecreationalBuildingData.buildArea} m<sup>2</sup>
                </Col>
              </Row>
              <Row>
                <Col span={SPAN_LABEL}>{t("calc.realty.enums.roofType._label")}</Col>
                <Col span={SPAN_VALUE}>
                  <Row>
                    <Col span={SPAN_VALUE}>
                      {t("calc.realty.enums.roofType." + calcData.houseOrRecreationalBuildingData.roofType)}
                      {`, `}
                      {t("calc.realty.attrs.houseOrRecreationalBuildingData.atticArea")}:{" "}
                      {calcData.houseOrRecreationalBuildingData.atticArea} m<sup>2</sup>
                      {`, `}
                      {calcData.houseOrRecreationalBuildingData.residentialAtticArea ? (
                        <>
                          {t("calc.realty.attrs.houseOrRecreationalBuildingData.residentialAtticArea")}:{" "}
                          {calcData.houseOrRecreationalBuildingData.residentialAtticArea} m<sup>2</sup>
                        </>
                      ) : undefined}
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row>
                <Col span={SPAN_LABEL}>{t("calc.realty.sections.overgroundFloorAreas")}</Col>
                <Col span={SPAN_VALUE}>
                  {Array.from(
                    { length: calcData.houseOrRecreationalBuildingData.overgroundFloorAreas.length },
                    (_, i) => i + 1,
                  ).map((floorNumber) => (
                    <span key={floorNumber}>
                      {floorNumber > 1 ? `, ` : undefined}
                      {tInterval(
                        "calc.realty.attrs.houseOrRecreationalBuildingData.overgroundFloorAreas_interval",
                        floorNumber,
                      )}
                      : {calcData.houseOrRecreationalBuildingData?.overgroundFloorAreas[floorNumber - 1]} m<sup>2</sup>
                    </span>
                  ))}
                </Col>
              </Row>
              <Row>
                <Col span={SPAN_LABEL}>{t("calc.realty.sections.areas")}</Col>
                <Col span={SPAN_VALUE}>
                  <span>
                    {t("calc.realty.attrs.houseOrRecreationalBuildingData.cellarArea")}:{" "}
                    {calcData.houseOrRecreationalBuildingData.cellarArea ?? 0} m<sup>2</sup>
                  </span>
                  {`, `}
                  <span>
                    {t("calc.realty.attrs.houseOrRecreationalBuildingData.residentialCellarArea")}:{" "}
                    {calcData.houseOrRecreationalBuildingData.residentialCellarArea ?? 0} m<sup>2</sup>
                  </span>
                  {`, `}
                  <span>
                    {t("calc.realty.attrs.houseOrRecreationalBuildingData.garageArea")}:{" "}
                    {calcData.houseOrRecreationalBuildingData.garageArea ?? 0} m<sup>2</sup>
                  </span>
                </Col>
              </Row>
            </>
          ) : undefined}
          {calcData.apartmentData ? (
            <>
              {calcData.apartmentData.constructionMaterial ? (
                <Row>
                  <Col span={SPAN_LABEL}>{t("calc.realty.enums.constructionMaterial._label")}</Col>
                  <Col span={SPAN_VALUE}>
                    {t("calc.realty.enums.constructionMaterial." + calcData.apartmentData.constructionMaterial)}
                  </Col>
                </Row>
              ) : undefined}
              <Row>
                <Col span={SPAN_LABEL}>{t("calc.realty.attrs.apartmentData.floor")}</Col>
                <Col span={SPAN_VALUE}>{calcData.apartmentData.floor}</Col>
              </Row>
              <Row>
                <Col span={SPAN_LABEL}>{t("calc.realty.attrs.apartmentData.apartmentNumber")}</Col>
                <Col span={SPAN_VALUE}>{calcData.apartmentData.apartmentNumber}</Col>
              </Row>
              <Row>
                <Col span={SPAN_LABEL}>{t("calc.realty.sections.areas")}</Col>
                <Col span={SPAN_VALUE}>
                  <span>
                    {t("calc.realty.attrs.apartmentData.floorArea")}: {calcData.apartmentData.floorArea} m<sup>2</sup>
                  </span>
                  {`,  `}
                  <span>
                    {t("calc.realty.sections.balconyFloorArea")}: {calcData.apartmentData.balconyArea ?? 0} m
                    <sup>2</sup>
                  </span>
                  {`,  `}
                  <span>
                    {t("calc.realty.attrs.apartmentData.terraceArea")}: {calcData.apartmentData.terraceArea ?? 0} m
                    <sup>2</sup>
                  </span>
                  {`,  `}
                  <span>
                    {t("calc.realty.attrs.apartmentData.cellarArea")}: {calcData.apartmentData.cellarArea ?? 0} m
                    <sup>2</sup>
                  </span>
                  {`,  `}
                  <span>
                    {t("calc.realty.attrs.apartmentData.garageArea")}: {calcData.apartmentData.garageArea ?? 0} m
                    <sup>2</sup>
                  </span>
                </Col>
              </Row>
            </>
          ) : undefined}
        </Col>
      </Row>

      <Row className="margin-top-small">
        <Col span={11}>
          <Divider titlePlacement="start">{t("calc.summary.sections.insuranceAndCoverages")}</Divider>
          <Row>
            <Col span={13}>
              <Row>
                <Col span={20}>{t("calc.realty.attrs.realtyInsuranceData._label")}</Col>
                <Col span={4}>
                  {calcData.realtyInsuranceData ? (
                    <AntIcon type="check-circle-two-tone" twoToneColor={cssVariables.colorGreen} />
                  ) : (
                    <AntIcon type="close-circle-two-tone" twoToneColor={cssVariables.colorRed} />
                  )}
                </Col>
              </Row>
            </Col>
            <Col span={11}>
              {calcData.realtyInsuranceData ? (
                <>
                  {t("calc.realty.attrs.realtyInsuranceData.insuranceAmount")}:{" "}
                  {isDefined(selectedCalcResult.data?.realtyInsuranceAmount)
                    ? formatLocaleCurrency(selectedCalcResult.data.realtyInsuranceAmount, 0)
                    : undefined}
                </>
              ) : undefined}
            </Col>
          </Row>

          <Row>
            <Col span={13}>
              <Row>
                <Col span={20}>{t("calc.realty.attrs.householdInsuranceData._label")}</Col>
                <Col span={4}>
                  {calcData.householdInsuranceData ? (
                    <AntIcon type="check-circle-two-tone" twoToneColor={cssVariables.colorGreen} />
                  ) : (
                    <AntIcon type="close-circle-two-tone" twoToneColor={cssVariables.colorRed} />
                  )}
                </Col>
              </Row>
            </Col>
            <Col span={11}>
              {calcData.householdInsuranceData ? (
                <>
                  {t("calc.realty.attrs.householdInsuranceData.insuranceAmount")}:{" "}
                  {isDefined(selectedCalcResult.data?.householdInsuranceAmount)
                    ? formatLocaleCurrency(selectedCalcResult.data.householdInsuranceAmount, 0)
                    : undefined}
                </>
              ) : undefined}
            </Col>
          </Row>

          <Row>
            <Col span={13}>
              <Row>
                <Col span={20}>{t("calc.realty.attrs.liabilityInsuranceData._label")}</Col>
                <Col span={4}>
                  {liabilityInsuranceAmount ? (
                    <AntIcon type="check-circle-two-tone" twoToneColor={cssVariables.colorGreen} />
                  ) : (
                    <AntIcon type="close-circle-two-tone" twoToneColor={cssVariables.colorRed} />
                  )}
                </Col>
              </Row>
            </Col>
            <Col span={11}>
              {liabilityInsuranceAmount ? (
                <>
                  {t("contract.attrs.insurances.insuranceData.insuranceAmount")}:{" "}
                  {formatLocaleCurrency(liabilityInsuranceAmount, 0)}
                </>
              ) : undefined}
            </Col>
          </Row>

          <Row>
            <Col span={13}>
              <Row>
                <Col span={20}>
                  {t(
                    selectedCalcResult.insuranceInstitution.institutionEnum === InstitutionEnum.PREMIUM
                      ? "calc.realty.results.complicityAltName"
                      : "calc.realty.results.complicity",
                  )}
                </Col>
                <Col span={4}>
                  {selectedCalcResult.data?.complicity ? (
                    <AntIcon type="check-circle-two-tone" twoToneColor={cssVariables.colorGreen} />
                  ) : (
                    <AntIcon type="close-circle-two-tone" twoToneColor={cssVariables.colorRed} />
                  )}
                </Col>
              </Row>
            </Col>
            <Col span={11}>
              {isDefined(selectedCalcResult.data?.complicity)
                ? formatLocaleCurrency(selectedCalcResult.data.complicity, 0)
                : undefined}
            </Col>
          </Row>
        </Col>

        <Col span={1} />

        <Col span={12}>
          <Divider titlePlacement="start">{t("calc.vehicle.results.additionalInfo")}</Divider>
          <Row>
            <Col span={11}>{t("calc.vehicle.attrs.generalData.effectiveBeginningDate")}</Col>
            <Col span={13}>{formatLocaleDate(calcData.generalInsuranceData.effectiveBeginningDate)}</Col>
          </Row>
          <Row>
            <Col span={11}>{t("contract.attrs.annualPremium")}</Col>
            <Col span={13}>
              {selectedCalcResult.data ? formatLocaleCurrency(selectedCalcResult.data.annualPremium) : undefined}
            </Col>
          </Row>
          <Row>
            <Col span={11}>{t("contract.enums.paymentFrequency._label")}</Col>
            <Col span={13}>
              {t("contract.enums.paymentFrequency." + calcData.generalInsuranceData.paymentFrequency)}
            </Col>
          </Row>
        </Col>
      </Row>

      <div className="margin-top-small">
        <GainersCommissionRateSection
          divideCommission={divideCommission}
          setDivideCommission={setDivideCommission}
          gainersDataForm={gainersDataForm}
        />
      </div>
    </div>
  );
};

export default RealtyCalcSummaryView;
